import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import FileInput from '@/components/ui/custom/input/FileInput';
import { Avatar, Button } from '@/components/ui/daisy';
import {
  DocumentPlusIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

type CoverImageInputProps = {
  value?: string;
  onChange: (url: string) => void;
  onFileSelect?: (file: File) => void;
  accept?: string;
  hideTrashButton?: boolean;
  limit?: number;
};

export default function CoverImageInput({
  value,
  onChange,
  onFileSelect,
  hideTrashButton,
  ...rest
}: CoverImageInputProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.form',
  });
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    onChange(previewUrl);
    onFileSelect?.(file);
  };

  const icon = isDragging ? (
    <DocumentPlusIcon className="w-6 h-6" />
  ) : (
    <PlusIcon className="w-6 h-6" />
  );

  return (
    <div className="relative w-fit h-fit flex items-center justify-center">
      <Avatar
        size="lg"
        shape="rounded"
        placeholder={!value}
        className={twMerge(isDragging && 'ring-2 ring-primary rounded-xl')}
      >
        {value ? (
          <img src={value} alt={t('courseCoverAlt')} className="object-cover" />
        ) : (
          icon
        )}
      </Avatar>
      <FileInput
        className="absolute inset-0 z-10 cursor-pointer w-full h-full opacity-0"
        accept=".png,.jpg,.jpeg"
        onChange={handleFileChange}
        onDragging={setIsDragging}
        {...rest}
      />
      <ConditionalRenderer condition={!!value && !hideTrashButton}>
        <Button
          shape="square"
          type="button"
          variant="ghost"
          className="absolute left-[calc(100%+0.5rem)]"
          onClick={() => onChange('')}
        >
          <TrashIcon className="w-6 h-6 text-error" />
        </Button>
      </ConditionalRenderer>
    </div>
  );
}
